/* istanbul ignore file - we have a test that tests the epics in general */
import * as _ from "lodash";
import { ajax, AjaxResponse } from "rxjs/ajax";
import { Observable } from "rxjs/internal/Observable";

import { memoize, Nullable, TS } from "@ctra/utils";
import { Request } from "../../../utils/ajax";

import { FetchChartDataPendingPayload } from "../actions";

import { buildURLWithQueryString, makeAzureApiURL, withSandboxPrefix } from "../../../utils/ajax";
import { ChartFilters } from "../typings";
import { EpicBase } from "./Base";
import { StateObservable } from "redux-observable";
import { EnterpriseAppState } from "../../../enterprise";

type DataPointsFilterType = Omit<ChartFilters, "farmIDs" | "unitSystem">;

/**
 * DataPoints Epic class
 */
class DataPointsEpic extends EpicBase {
  /**
   * Make a request URL (for exposing to the components when needed for publishing)
   * @returns {string}
   */
  @memoize
  makeRequestURL(
    {
      state$
    }: {
      state$: StateObservable<EnterpriseAppState>;
      Request: typeof Request;
    },
    payload: Omit<FetchChartDataPendingPayload, "hash" | "sourceType">
  ): string {
    const {
      chartID,
      timePeriod: { startDate, endDate },
      bucketSize,
      farmIDs,
      unitSystem,
      filters = {}
    } = payload;

    const [variant, chartType] = chartID.split("-");

    const {
      flags: { isMultiDataIntervalSupported }
    } = state$.value.dataDictionary.dataDescriptors[variant];

    const filterQuery = this.serializeFilterQuery(this.buildFiltersQuery(filters));
    const includeProjected = TS.asMoment(endDate).isAfter(TS.asMoment(TS.now()).endOf("day"));

    const body = _.omitBy(
      {
        chartType,
        unitSystem,
        variantId: variant,
        farmIds: _.join(farmIDs, ","),
        from: startDate,
        to: endDate,
        bucketSize: isMultiDataIntervalSupported ? bucketSize : void 0,
        filters: filterQuery,
        options: _.compact([includeProjected ? "includeProjected" : null])
      },
      _.isNil
    );

    const baseUrl = makeAzureApiURL(withSandboxPrefix("analytics", state$), "/metrics/data")();

    return buildURLWithQueryString(baseUrl, body);
  }

  @memoize
  makeRequest(
    payload: Omit<FetchChartDataPendingPayload, "hash" | "sourceType">,
    { Request: R, state$ }: { Request: typeof Request; state$: StateObservable<EnterpriseAppState> }
  ) {
    const {
      dataURL,
      chartID,
      timePeriod: { startDate, endDate },
      bucketSize,
      farmIDs,
      unitSystem,
      filters = {}
    } = payload;

    const [variant, chartType] = chartID.split("-");

    const {
      flags: { isMultiDataIntervalSupported }
    } = state$.value.dataDictionary.dataDescriptors[variant];

    const url = makeAzureApiURL(withSandboxPrefix("analytics", state$), "/metrics/data")();
    const filterQuery = this.serializeFilterQuery(this.buildFiltersQuery(filters));
    const includeProjected = TS.asMoment(endDate).isAfter(TS.asMoment(TS.now()).endOf("day"));

    return dataURL
      ? ajax.get(dataURL)
      : R.GET(url, {
          body: _.omitBy(
            {
              chartType,
              unitSystem,
              variantId: variant,
              farmIds: _.join(farmIDs, ","),
              from: startDate,
              to: endDate,
              bucketSize: isMultiDataIntervalSupported ? bucketSize : void 0,
              filters: filterQuery,
              options: _.compact([includeProjected ? "includeProjected" : null])
            },
            _.isNil
          )
        });
  }

  /**
   * Build the filter query to match the new filter query structure expected on the BE
   * e.g filters=herdGroupId:$in[10,100]+dimGroupKey:$in[[-30:0]]
   *
   * @note Given that this custom and doesn't follow any popular atandard,
   * a small package should be built around this on the BE and FE, because at the moment,
   * if a change is made on the expected structure on the BE without a corresponding change on the FE
   * the requests will fail.
   * @param filters
   * @private
   * @returns
   */
  private buildFiltersQuery = <T extends DataPointsFilterType>(filters: T): Record<keyof T, string> => {
    return _.reduce<T, Record<keyof T, string>>(
      //@ts-ignore
      _.omitBy(filters, _.overSome(_.isNil, _.isEmpty)),
      (acc, value, key) => {
        const eqFilter = _.isArray(value) && _.size(value) === 1;

        const formattedValue = _.isArray(value)
          ? _.join(_.map(value, (item) => JSON.stringify(item)))
          : JSON.stringify(value);

        acc[key as keyof T] = eqFilter ? `$eq[${formattedValue}]` : `$in[${formattedValue}]`;

        return acc;
      },
      {}
    );
  };

  private serializeFilterQuery = (
    filterQuery: Record<keyof DataPointsFilterType, string>
  ): Nullable<string> => {
    return (
      _.reduce(
        filterQuery,
        (acc, v, k) => {
          acc += `${acc === "" ? "" : "+"}${k}:${v}`;

          return acc;
        },
        ""
      ) || null
    );
  };
}

export { DataPointsEpic };
